/* The review header strip: the P3-09 header block, collapsed.
 *
 * These are the fields the FEMP ESPC Project Review Comment Template prints
 * above the comment table — see docs/redesign/P3-09.md — field for field and in
 * the order the form prints them. They are here because the export needs them
 * and only the reviewer knows them.
 *
 * Nothing blocks on them. The strip is collapsed on arrival, every field is
 * optional, and a review can be worked from end to end without opening it. */

import type { ReactNode } from 'react';

import { Disclosure, Label } from '../primitives';
import { SUBMITTAL_TYPES } from '../../data/review';
import type { HeaderFields, SubmittalType } from '../../types';
import './ReviewDetails.css';

export interface ReviewDetailsProps {
  fields: HeaderFields;
  onChange(patch: Partial<HeaderFields>): void;
  /** Open on first render. Collapsed is the default: nothing blocks on these. */
  defaultOpen?: boolean;
  /**
   * Anything else that belongs to this review rather than to a finding, drawn
   * under the note. Today that is the control that deletes the review: it is
   * about the review as a whole, and this strip is the only thing in the
   * workspace that is.
   */
  children?: ReactNode;
}

/** The label on the strip, so the copy is asserted rather than trusted. */
export const REVIEW_DETAILS_LABEL =
  'Review details — Project no., contract no., due date, reviewer';

export default function ReviewDetails({
  fields,
  onChange,
  defaultOpen = false,
  children,
}: ReviewDetailsProps) {
  return (
    <Disclosure
      className="rd"
      defaultOpen={defaultOpen}
      headClassName="rd__head"
      panelClassName="rd__body"
      summary={
        <>
          Review details
          <span className="rd__hint">
            {' '}
            — Project no., contract no., due date, reviewer
          </span>
        </>
      }
    >
      <div className="rd__grid">
        <Field
          label="Project Name/No."
          value={fields.projectName}
          onChange={(projectName) => onChange({ projectName })}
        />
        <Field
          label="Contract No."
          value={fields.contractNo}
          onChange={(contractNo) => onChange({ contractNo })}
        />
        <Field
          label="Date of Document"
          type="date"
          value={fields.documentDate}
          onChange={(documentDate) => onChange({ documentDate })}
        />
        <Field
          label="Comments Due By"
          type="date"
          value={fields.commentsDueBy}
          onChange={(commentsDueBy) => onChange({ commentsDueBy })}
        />

        <label className="rd__field rd__field--wide">
          <Label as="span">Type of Submittal/Deliverable</Label>
          <select
            className="rd__input"
            value={fields.submittalType}
            onChange={(e) =>
              onChange({ submittalType: e.target.value as SubmittalType })
            }
          >
            {SUBMITTAL_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </label>

        {fields.submittalType === 'other' ? (
          <Field
            label="Which one"
            value={fields.submittalOther}
            onChange={(submittalOther) => onChange({ submittalOther })}
          />
        ) : null}

        <Field
          label="Reviewer"
          value={fields.reviewerName}
          onChange={(reviewerName) => onChange({ reviewerName })}
        />
        <Field
          label="Date"
          type="date"
          value={fields.reviewerDate}
          onChange={(reviewerDate) => onChange({ reviewerDate })}
        />
        <Field
          label="Phone"
          type="tel"
          value={fields.reviewerPhone}
          onChange={(reviewerPhone) => onChange({ reviewerPhone })}
        />
        <Field
          label="Email"
          type="email"
          value={fields.reviewerEmail}
          onChange={(reviewerEmail) => onChange({ reviewerEmail })}
        />
      </div>

      <p className="rd__note">
        These go at the top of the comment sheet. Nothing here has to be filled
        in to work through the findings, and nothing here leaves this browser.
      </p>

      {children}
    </Disclosure>
  );
}

interface FieldProps {
  label: string;
  value: string;
  type?: 'text' | 'date' | 'tel' | 'email';
  onChange(next: string): void;
}

function Field({ label, value, type = 'text', onChange }: FieldProps) {
  return (
    <label className="rd__field">
      <Label as="span">{label}</Label>
      <input
        className="rd__input"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
